import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Iter 77 — coverage editors. Generic building blocks that close the
 * "everything visible must be editable" gaps:
 *
 *  • FormCopyEditor  — manifest-driven editor for ALL visible copy inside a
 *    form (labels / placeholders / submit / footnotes / success text).
 *    Writes `website_content.copy_overrides.<key>` via updateField so the
 *    canvas live-previews and the TopBar Save persists. Opening it never
 *    writes anything (Iter 71B contract).
 *  • List editors — canonical list-typed fields (service_areas,
 *    business_hours, about_values, about_stats, hero_stats) saved atomically
 *    via saveWebsiteContentList.
 *  • CategoryTilesEditor / ReservationInfoEditor / RestaurantContactEditor —
 *    composite editors mixing canonical fields + copy overrides.
 */

/* ────────────────────────── Form copy manifests ───────────────────────── */

export const FORM_MANIFESTS = {
  service_consultation: {
    title: "Consultation form",
    prefix: "forms.consultation.",
    fields: [
      { key: "nameLabel", label: "Name label", def: "Full Name *" },
      { key: "namePlaceholder", label: "Name placeholder", def: "Jane Doe" },
      { key: "phoneLabel", label: "Phone label", def: "Phone *" },
      { key: "phonePlaceholder", label: "Phone placeholder", def: "(555) 000-1234" },
      { key: "emailLabel", label: "Email label", def: "Email *" },
      { key: "emailPlaceholder", label: "Email placeholder", def: "jane@email.com" },
      { key: "serviceLabel", label: "Service label", def: "Service Needed" },
      { key: "servicePlaceholder", label: "Service placeholder", def: "Choose a service (or leave blank)" },
      { key: "dateLabel", label: "Date label", def: "Preferred Date" },
      { key: "timeLabel", label: "Time label", def: "Preferred Time" },
      { key: "timePlaceholder", label: "Time placeholder", def: "Anytime that works" },
      { key: "timeOptions", label: "Time options (comma-separated)", def: "Morning (8am – 12pm), Afternoon (12pm – 5pm), Evening (5pm – 7pm), Weekend" },
      { key: "messageLabel", label: "Message label", def: "Tell us what's going on" },
      { key: "messagePlaceholder", label: "Message placeholder", def: "Describe the project or problem in your own words. No jargon required.", textarea: true },
      { key: "submitLabel", label: "Submit button", def: "Submit Consultation Request" },
      { key: "footnote", label: "Footnote", def: "We reply within 60 minutes during business hours. No spam, ever.", textarea: true },
      { key: "successTitle", label: "Success message", def: "Consultation request received!" },
      { key: "successBody", label: "Success detail", def: "A real person will reach out within the hour to confirm your time.", textarea: true },
    ],
  },
  service_contact: {
    title: "Contact form",
    prefix: "forms.contact.",
    fields: [
      { key: "nameLabel", label: "Name label", def: "Full Name *" },
      { key: "namePlaceholder", label: "Name placeholder", def: "Jane Doe" },
      { key: "phoneLabel", label: "Phone label", def: "Phone" },
      { key: "phonePlaceholder", label: "Phone placeholder", def: "(555) 000-1234" },
      { key: "emailLabel", label: "Email label", def: "Email *" },
      { key: "emailPlaceholder", label: "Email placeholder", def: "jane@email.com" },
      { key: "messageLabel", label: "Message label", def: "How can we help? *" },
      { key: "messagePlaceholder", label: "Message placeholder", def: "Tell us what you need — a repair, a quote, a question…", textarea: true },
      { key: "submitLabel", label: "Submit button", def: "Send Message" },
      { key: "successTitle", label: "Success message", def: "Message received!" },
      { key: "successBody", label: "Success detail", def: "We'll get back to you within the hour.", textarea: true },
    ],
  },
  service_consult_expect: {
    title: "What to expect cards",
    prefix: "consultations.expect.",
    fields: [
      { key: "item1Title", label: "Card 1 title", def: "A real conversation" },
      { key: "item1Body", label: "Card 1 text", def: "We start with the problem in your words — no jargon, no upsell pressure.", textarea: true },
      { key: "item2Title", label: "Card 2 title", def: "An honest diagnosis" },
      { key: "item2Body", label: "Card 2 text", def: "We inspect the work area, explain what we see, and share the actual root cause.", textarea: true },
      { key: "item3Title", label: "Card 3 title", def: "A written quote" },
      { key: "item3Body", label: "Card 3 text", def: "Itemized labor + materials. No surprises. The price we quote is the price you pay.", textarea: true },
      { key: "item4Title", label: "Card 4 title", def: "A clear next step" },
      { key: "item4Body", label: "Card 4 text", def: "If you're in, we schedule it. If not, no hard feelings — we're neighbors either way.", textarea: true },
    ],
  },
  service_reviews_stats: {
    title: "Trust stats",
    prefix: "reviews.stats.",
    fields: [
      { key: "stat1K", label: "Stat 1 value", def: "4.9★" },
      { key: "stat1V", label: "Stat 1 label", def: "Average rating" },
      { key: "stat2K", label: "Stat 2 value", def: "300+" },
      { key: "stat2V", label: "Stat 2 label", def: "Homes served" },
      { key: "stat3K", label: "Stat 3 value", def: "15 yrs" },
      { key: "stat3V", label: "Stat 3 label", def: "Locally owned" },
      { key: "stat4K", label: "Stat 4 value", def: "100%" },
      { key: "stat4V", label: "Stat 4 label", def: "Licensed & insured" },
    ],
  },
  ecom_newsletter: {
    title: "Newsletter form",
    prefix: "newsletter.",
    fields: [
      { key: "placeholder", label: "Email placeholder", def: "your@email.com" },
      { key: "submitLabel", label: "Subscribe button", def: "Subscribe" },
      { key: "successMessage", label: "Success message", def: "You're in. Watch for the next drop." },
      { key: "errorMessage", label: "Error message", def: "Couldn't subscribe. Try again." },
    ],
  },
  ecom_shop_toolbar: {
    title: "Shop filters & sort",
    prefix: "shop.",
    fields: [
      { key: "filterAllLabel", label: "\"All\" filter label", def: "All" },
      { key: "filtersLabel", label: "Filters button", def: "Filters" },
      { key: "sortLabel", label: "Sort label", def: "Sort" },
      { key: "sortFeatured", label: "Sort: featured", def: "Featured" },
      { key: "sortPriceAsc", label: "Sort: price low→high", def: "Price: Low → High" },
      { key: "sortPriceDesc", label: "Sort: price high→low", def: "Price: High → Low" },
      { key: "sortTopRated", label: "Sort: top rated", def: "Top Rated" },
      { key: "resultsPrefix", label: "Search results prefix", def: "Results for" },
    ],
  },
  restaurant_reservation: {
    title: "Reservation form",
    prefix: "forms.reservation.",
    fields: [
      { key: "nameLabel", label: "Name label", def: "Full name" },
      { key: "namePlaceholder", label: "Name placeholder", def: "Your name" },
      { key: "emailLabel", label: "Email label", def: "Email" },
      { key: "emailPlaceholder", label: "Email placeholder", def: "you@email.com" },
      { key: "phoneLabel", label: "Phone label", def: "Phone" },
      { key: "phonePlaceholder", label: "Phone placeholder", def: "+1 …" },
      { key: "dateLabel", label: "Date label", def: "Date" },
      { key: "datePlaceholder", label: "Date placeholder", def: "Select a date" },
      { key: "timeLabel", label: "Time label", def: "Time" },
      { key: "timePlaceholder", label: "Time placeholder", def: "Select a seating" },
      { key: "guestsLabel", label: "Guests label", def: "Guests" },
      { key: "guestsSuffix", label: "Guests suffix", def: "guests" },
      { key: "occasionLabel", label: "Occasion label", def: "Occasion" },
      { key: "occasionPlaceholder", label: "Occasion placeholder", def: "No occasion" },
      { key: "notesLabel", label: "Requests label", def: "Special requests" },
      { key: "notesPlaceholder", label: "Requests placeholder", def: "Dietary preferences, allergies, accessibility…", textarea: true },
      { key: "submitLabel", label: "Submit button", def: "Request a table" },
      { key: "disclaimer", label: "Disclaimer", def: "Submitting a request does not guarantee a seating. You will receive a confirmation once our team reviews availability.", textarea: true },
      { key: "successEyebrow", label: "Success eyebrow", def: "Request received" },
    ],
  },
};

export function FormCopyEditor({ formKey, draft, publicPayload, updateField }) {
  const manifest = FORM_MANIFESTS[formKey];
  const overrides = useMemo(() => ({
    ...(publicPayload?.website_content?.copy_overrides || {}),
    ...(draft?.copy_overrides || {}),
  }), [publicPayload, draft]);
  if (!manifest) return <p className="text-xs text-gray-500">Unknown form.</p>;

  return (
    <div className="space-y-3" data-testid={`form-copy-editor-${formKey}`}>
      <p className="text-[11px] text-gray-500 leading-relaxed">
        Every visible piece of text in this form is editable below. The form's
        submit behavior stays untouched.
      </p>
      {manifest.fields.map((f) => {
        const path = manifest.prefix + f.key;
        const val = overrides[path] ?? f.def ?? "";
        const C = f.textarea ? Textarea : Input;
        return (
          <div key={f.key}>
            <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1">{f.label}</label>
            <C
              value={val}
              onChange={(e) => updateField(`copy_overrides.${path}`, e.target.value)}
              data-testid={`form-copy-${formKey}-${f.key}`}
            />
          </div>
        );
      })}
      <p className="text-[11px] text-gray-500">Press <b>Save</b> in the top bar to persist.</p>
    </div>
  );
}

/* ─────────────────────── Generic list editor bits ─────────────────────── */

function useListState(publicPayload, wcKey, derive) {
  const initial = useMemo(() => {
    const stored = publicPayload?.website_content?.[wcKey];
    if (Array.isArray(stored) && stored.length) return stored;
    return derive ? (derive(publicPayload) || []) : [];
  }, [publicPayload, wcKey, derive]);
  const [items, setItems] = useState(initial);
  return [items, setItems];
}

function ListShell({ children, onSave, saving, testId }) {
  return (
    <div className="space-y-3" data-testid={testId}>
      {children}
      <Button onClick={onSave} disabled={saving} className="w-full" data-testid={`${testId}-save`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

function RowControls({ i, len, onMove, onRemove, testId }) {
  return (
    <div className="flex flex-col gap-0.5">
      <button onClick={() => onMove(i, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-30" data-testid={`${testId}-up-${i}`}><ArrowUp className="w-3 h-3" /></button>
      <button onClick={() => onMove(i, 1)} disabled={i === len - 1} className="p-0.5 disabled:opacity-30" data-testid={`${testId}-down-${i}`}><ArrowDown className="w-3 h-3" /></button>
      <button onClick={() => onRemove(i)} className="p-0.5 text-red-500" data-testid={`${testId}-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

function makeMove(setItems) {
  return (i, dir) => setItems((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
}

async function saveList(saveAll, patch, setSaving) {
  setSaving(true);
  try {
    await saveAll(patch);
    toast.success("Saved.");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Couldn't save");
  } finally {
    setSaving(false);
  }
}

/* ─── Simple string lists: service areas (service) + hero stats (rest.) ── */

function StringListEditor({ publicPayload, saveAll, wcKey, derive, addLabel, testId }) {
  const [items, setItems] = useListState(publicPayload, wcKey, derive);
  const [saving, setSaving] = useState(false);
  const move = makeMove(setItems);
  return (
    <ListShell onSave={() => saveList(saveAll, { [wcKey]: items.filter((s) => (s || "").trim()) }, setSaving)} saving={saving} testId={testId}>
      {items.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={s} onChange={(e) => setItems((arr) => arr.map((x, xi) => (xi === i ? e.target.value : x)))} data-testid={`${testId}-input-${i}`} />
          <RowControls i={i} len={items.length} onMove={move} onRemove={(ri) => setItems((arr) => arr.filter((_, xi) => xi !== ri))} testId={testId} />
        </div>
      ))}
      <button onClick={() => setItems((arr) => [...arr, ""])} className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs font-bold text-gray-600 hover:border-black flex items-center justify-center gap-1" data-testid={`${testId}-add`}>
        <Plus className="w-3 h-3" /> {addLabel}
      </button>
    </ListShell>
  );
}

export function ServiceAreasEditor({ publicPayload, saveAll }) {
  return (
    <StringListEditor
      publicPayload={publicPayload}
      saveAll={saveAll}
      wcKey="service_areas"
      derive={(p) => p?.service_config?.site?.serviceAreas}
      addLabel="Add area"
      testId="service-areas-editor"
    />
  );
}

export function HeroStatsEditor({ publicPayload, saveAll }) {
  return (
    <StringListEditor
      publicPayload={publicPayload}
      saveAll={saveAll}
      wcKey="hero_stats"
      derive={(p) => p?.restaurant_config?.stats}
      addLabel="Add line"
      testId="hero-stats-editor"
    />
  );
}

/* ─── Pair lists: business hours, about values, about stats ────────────── */

function PairListEditor({ publicPayload, saveAll, wcKey, derive, fieldA, fieldB, textareaB, addLabel, testId }) {
  const [items, setItems] = useListState(publicPayload, wcKey, derive);
  const [saving, setSaving] = useState(false);
  const move = makeMove(setItems);
  const setF = (i, k, v) => setItems((arr) => arr.map((x, xi) => (xi === i ? { ...x, [k]: v } : x)));
  const B = textareaB ? Textarea : Input;
  return (
    <ListShell onSave={() => saveList(saveAll, { [wcKey]: items.filter((x) => ((x[fieldA.key] || "") + (x[fieldB.key] || "")).trim()) }, setSaving)} saving={saving} testId={testId}>
      {items.map((x, i) => (
        <div key={i} className="flex items-start gap-2 p-2 rounded-lg border border-gray-200">
          <div className="flex-1 space-y-1.5">
            <Input placeholder={fieldA.label} value={x[fieldA.key] || ""} onChange={(e) => setF(i, fieldA.key, e.target.value)} data-testid={`${testId}-a-${i}`} />
            <B placeholder={fieldB.label} value={x[fieldB.key] || ""} onChange={(e) => setF(i, fieldB.key, e.target.value)} data-testid={`${testId}-b-${i}`} />
          </div>
          <RowControls i={i} len={items.length} onMove={move} onRemove={(ri) => setItems((arr) => arr.filter((_, xi) => xi !== ri))} testId={testId} />
        </div>
      ))}
      <button onClick={() => setItems((arr) => [...arr, { [fieldA.key]: "", [fieldB.key]: "" }])} className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs font-bold text-gray-600 hover:border-black flex items-center justify-center gap-1" data-testid={`${testId}-add`}>
        <Plus className="w-3 h-3" /> {addLabel}
      </button>
    </ListShell>
  );
}

export function BusinessHoursEditor({ publicPayload, saveAll }) {
  return (
    <PairListEditor
      publicPayload={publicPayload} saveAll={saveAll}
      wcKey="business_hours"
      derive={(p) => p?.service_config?.site?.hours}
      fieldA={{ key: "day", label: "Days (e.g. Mon – Fri)" }}
      fieldB={{ key: "time", label: "Hours (e.g. 8:00 AM – 6:00 PM)" }}
      addLabel="Add row" testId="business-hours-editor"
    />
  );
}

export function AboutValuesEditor({ publicPayload, saveAll }) {
  return (
    <PairListEditor
      publicPayload={publicPayload} saveAll={saveAll}
      wcKey="about_values"
      derive={(p) => {
        const items = p?.service_config?.copy?.about?.values?.items;
        if (Array.isArray(items) && items.length) return items;
        return [
          { title: "Neighbors, not customers", body: "We live where we work. Every job is a chance to earn a lifelong customer — and often, a friend." },
          { title: "Craft over speed", body: "We'd rather finish a job right on Tuesday than half-right on Monday. Every detail gets the same care." },
          { title: "Built to last", body: "Locally owned since 2011, growing only by word of mouth. No corporate parent, no rotating techs." },
        ];
      }}
      fieldA={{ key: "title", label: "Card title" }}
      fieldB={{ key: "body", label: "Card text" }}
      textareaB addLabel="Add value" testId="about-values-editor"
    />
  );
}

export function AboutStatsEditor({ publicPayload, saveAll }) {
  return (
    <PairListEditor
      publicPayload={publicPayload} saveAll={saveAll}
      wcKey="about_stats"
      derive={(p) => {
        const s = p?.service_config?.copy?.about?.trust?.stats;
        if (Array.isArray(s) && s.length) return s;
        return [
          { k: "15+", v: "Years serving locals" },
          { k: "4.9★", v: "Average review" },
          { k: "300+", v: "Homes helped" },
        ];
      }}
      fieldA={{ key: "k", label: "Value (e.g. 15+)" }}
      fieldB={{ key: "v", label: "Label (e.g. Years serving locals)" }}
      addLabel="Add stat" testId="about-stats-editor"
    />
  );
}

/* ─── Ecommerce: category tiles (label overrides + tile images) ────────── */

export function CategoryTilesEditor({ publicPayload, saveAll }) {
  const categories = publicPayload?.ecommerce_config?.categories || [];
  const wc = publicPayload?.website_content || {};
  const overrides = wc.copy_overrides || {};
  const storedImages = wc.category_tile_images || {};
  const [labels, setLabels] = useState(() => Object.fromEntries(
    categories.map((c) => [c.key, overrides[`categories.${c.key}.label`] || c.label || ""])
  ));
  const [images, setImages] = useState(() => Object.fromEntries(
    categories.map((c) => [c.key, storedImages[c.key] || c.image || ""])
  ));
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const upload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data?.image_url) setImages((m) => ({ ...m, [key]: data.image_url }));
    } catch (e) {
      toast.error("Couldn't upload image");
    } finally {
      setUploadingKey(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const nextOverrides = { ...overrides };
      for (const c of categories) {
        const v = (labels[c.key] || "").trim();
        if (v && v !== c.label) nextOverrides[`categories.${c.key}.label`] = v;
        else if (v === c.label) delete nextOverrides[`categories.${c.key}.label`];
      }
      const nextImages = {};
      for (const [k, v] of Object.entries(images)) if ((v || "").trim()) nextImages[k] = v.trim();
      await saveAll({ copy_overrides: nextOverrides, category_tile_images: nextImages });
      toast.success("Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (!categories.length) {
    return <p className="text-xs text-gray-500">Category tiles come from your product categories. Add categories to your products first.</p>;
  }
  return (
    <div className="space-y-3" data-testid="category-tiles-editor">
      <p className="text-[11px] text-gray-500 leading-relaxed">
        Tiles follow your product categories. Rename how a category displays and
        give each tile its own image.
      </p>
      {categories.map((c) => (
        <div key={c.key} className="p-2 rounded-lg border border-gray-200 space-y-1.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-gray-500">{c.key}</div>
          <Input value={labels[c.key] || ""} onChange={(e) => setLabels((m) => ({ ...m, [c.key]: e.target.value }))} placeholder="Display label" data-testid={`category-tile-label-${c.key}`} />
          <div className="flex items-center gap-2">
            <Input value={images[c.key] || ""} onChange={(e) => setImages((m) => ({ ...m, [c.key]: e.target.value }))} placeholder="Image URL" data-testid={`category-tile-image-${c.key}`} />
            <label className="text-[11px] font-bold px-2 py-2 rounded-md border border-gray-300 cursor-pointer whitespace-nowrap hover:bg-gray-50">
              {uploadingKey === c.key ? <Loader2 className="w-3 h-3 animate-spin" /> : "Upload"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(c.key, e.target.files?.[0])} data-testid={`category-tile-upload-${c.key}`} />
            </label>
          </div>
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="category-tiles-editor-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

/* ─── Restaurant: reservation info card (canonical + label overrides) ──── */

export function ReservationInfoEditor({ publicPayload, saveAll }) {
  const wc = publicPayload?.website_content || {};
  const cfg = publicPayload?.restaurant_config || {};
  const overrides = wc.copy_overrides || {};
  const res = cfg.reservations || {};
  const [seatings, setSeatings] = useState(() => {
    const s = Array.isArray(wc.seatings) && wc.seatings.length ? wc.seatings : (res.seatings || []);
    return s.map((x) => ({ time: x.time || "", label: x.label || "" }));
  });
  const [nights, setNights] = useState(wc.open_nights ?? res.nights ?? "");
  const [duration, setDuration] = useState(wc.meal_duration ?? res.duration ?? "");
  const [labels, setLabels] = useState({
    seatings_label: overrides["reservations.seatings_label"] || res.seatings_label || "Seatings",
    nights_label: overrides["reservations.nights_label"] || res.nights_label || "Nights",
    duration_label: overrides["reservations.duration_label"] || res.duration_label || "Duration",
    note: overrides["reservations.note"] || res.note || "",
  });
  const [saving, setSaving] = useState(false);
  const move = makeMove(setSeatings);

  const save = async () => {
    setSaving(true);
    try {
      const nextOverrides = { ...overrides };
      nextOverrides["reservations.seatings_label"] = labels.seatings_label;
      nextOverrides["reservations.nights_label"] = labels.nights_label;
      nextOverrides["reservations.duration_label"] = labels.duration_label;
      if ((labels.note || "").trim()) nextOverrides["reservations.note"] = labels.note;
      await saveAll({
        seatings: seatings.filter((s) => (s.time || s.label || "").trim()),
        open_nights: nights || "",
        meal_duration: duration || "",
        copy_overrides: nextOverrides,
      });
      toast.success("Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="reservation-info-editor">
      <Field label="Seatings heading"><Input value={labels.seatings_label} onChange={(e) => setLabels((m) => ({ ...m, seatings_label: e.target.value }))} data-testid="res-info-seatings-label" /></Field>
      {seatings.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input placeholder="Time (18:00)" value={s.time} onChange={(e) => setSeatings((arr) => arr.map((x, xi) => (xi === i ? { ...x, time: e.target.value } : x)))} data-testid={`res-info-seating-time-${i}`} />
          <Input placeholder="Label (First seating)" value={s.label} onChange={(e) => setSeatings((arr) => arr.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} data-testid={`res-info-seating-label-${i}`} />
          <RowControls i={i} len={seatings.length} onMove={move} onRemove={(ri) => setSeatings((arr) => arr.filter((_, xi) => xi !== ri))} testId="res-info-seating" />
        </div>
      ))}
      <button onClick={() => setSeatings((arr) => [...arr, { time: "", label: "" }])} className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs font-bold text-gray-600 hover:border-black flex items-center justify-center gap-1" data-testid="res-info-seating-add">
        <Plus className="w-3 h-3" /> Add seating
      </button>
      <Field label="Nights heading"><Input value={labels.nights_label} onChange={(e) => setLabels((m) => ({ ...m, nights_label: e.target.value }))} data-testid="res-info-nights-label" /></Field>
      <Field label="Nights"><Input value={nights} onChange={(e) => setNights(e.target.value)} placeholder="Tuesday through Sunday" data-testid="res-info-nights" /></Field>
      <Field label="Duration heading"><Input value={labels.duration_label} onChange={(e) => setLabels((m) => ({ ...m, duration_label: e.target.value }))} data-testid="res-info-duration-label" /></Field>
      <Field label="Duration"><Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Approximately 2 hours" data-testid="res-info-duration" /></Field>
      <Field label="Note"><Textarea value={labels.note} onChange={(e) => setLabels((m) => ({ ...m, note: e.target.value }))} data-testid="res-info-note" /></Field>
      <Button onClick={save} disabled={saving} className="w-full" data-testid="reservation-info-editor-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

/* ─── Restaurant: contact block (canonical + label overrides) ──────────── */

export function RestaurantContactEditor({ publicPayload, saveAll }) {
  const wc = publicPayload?.website_content || {};
  const cfg = publicPayload?.restaurant_config || {};
  const overrides = wc.copy_overrides || {};
  const ct = cfg.contact || {};
  const [vals, setVals] = useState({
    contact_address: wc.contact_address ?? (ct.address_lines || []).join(", "),
    hours_summary: wc.hours_summary ?? (ct.hours_lines || []).join(" · "),
    contact_phone: wc.contact_phone ?? ct.phone ?? "",
    contact_email: wc.contact_email ?? ct.email ?? "",
    private_events_email: wc.private_events_email ?? ct.private_events_email ?? "",
  });
  const [labels, setLabels] = useState({
    address_label: overrides["contact.address_label"] || ct.address_label || "Address",
    hours_label: overrides["contact.hours_label"] || ct.hours_label || "Hours",
    reach_label: overrides["contact.reach_label"] || ct.reach_label || "Reach us",
    private_events_label: overrides["contact.private_events_label"] || ct.private_events_label || "Private events",
    private_events_blurb: overrides["contact.private_events_blurb"] || ct.private_events_blurb || "For buyouts and private dinners, write to us at",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const nextOverrides = { ...overrides };
      for (const [k, v] of Object.entries(labels)) nextOverrides[`contact.${k}`] = v;
      await saveAll({ ...vals, copy_overrides: nextOverrides });
      toast.success("Saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const V = (k, label, ph) => (
    <Field label={label}><Input value={vals[k]} onChange={(e) => setVals((m) => ({ ...m, [k]: e.target.value }))} placeholder={ph} data-testid={`rest-contact-${k}`} /></Field>
  );
  const L = (k, label) => (
    <Field label={label}><Input value={labels[k]} onChange={(e) => setLabels((m) => ({ ...m, [k]: e.target.value }))} data-testid={`rest-contact-label-${k}`} /></Field>
  );

  return (
    <div className="space-y-3" data-testid="restaurant-contact-editor">
      {L("address_label", "Address heading")}
      {V("contact_address", "Address", "123 Main St, Brooklyn")}
      {L("hours_label", "Hours heading")}
      {V("hours_summary", "Hours", "Tue — Sun · Dinner from 18:00")}
      {L("reach_label", "Reach-us heading")}
      {V("contact_phone", "Phone", "+1 (555) 000-1234")}
      {V("contact_email", "Email", "hello@restaurant.com")}
      {L("private_events_label", "Private events heading")}
      {L("private_events_blurb", "Private events text")}
      {V("private_events_email", "Private events email", "events@restaurant.com")}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="restaurant-contact-editor-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
